import { createStore } from 'vuex'
import { envName, lang } from '../plugins/utils'
import { getTextData, getAuthInfo, getWalletList, getWalletUserNotifications, getExchangeRates, doExchange } from '../services/api'
import { handleAsyncError } from '../services/errorHandler'

const fetchWalletIdInLocalStorage = {
  set: (id) => {
    localStorage.setItem(`${envName}ActiveWalletId`, id)
  },
  get: () => {
    return localStorage.getItem(`${envName}ActiveWalletId`)
  }
}

async function refreshWalletList(context) {
  const walletList = await getWalletList(context.state.authToken)
  context.commit("updateWalletList", Array.isArray(walletList) ? walletList : [])

  return walletList
}

const store = createStore({
  state: {
    sendConfirmCode: false,
    phone: '',
    authToken: localStorage.getItem("authToken"),
    walletList: null,  
    authInfo: null,
    authInfoLoading: false,
    textData: null,
    userNotifications: null,
    openSearchAssets: false,
    openSelectAccount: false,
    selectExchangeOpened: false,
    selectExchangeActiveTab: null,
    accountsLoading: true,
    exchangeFromAccount: null,
    exchangeToAccount: null,
    exchangeRates: null,
    lang: lang,
    currentWallet: null,
  },
  getters: {
    authInfoLoading: state => !!state.authInfoLoading,
    hasAuthInfo: state => !!state.authInfo,
    kycPassed: (state) => {
      return !!state.authInfo && state.authInfo.identification_level.level !== 0
    },
    textData: (state) => (textId) => {
      if (!textId || !state.textData || !state.textData[textId] || !state.lang) {
        return null;
      }
      return state.textData[textId][state.lang];
    },
    activesByCurrencies: state => {
      if (!state.walletList) return []

      if (state.exchangeRates) {
        // const exchangeRates = [
        //   ...state.exchangeRates.rates,
        //   {
        //     "id": "0195f72b-0ed4-7cb8-5c11-a33acec35a62",
        //     "from_currency_code": "USDC",
        //     "to_currency_code": "EUR",
        //     "quantity_in": "1.0000",
        //     "quantity_out": "0.917",
        //     "min_amount": "0.0000",
        //     "max_amount": "0.0000"
        //   }
        // ]

        const exchangeRates = state.exchangeRates.rates
        return state.walletList.reduce((acc, item) => {
          const { currency_code } = item
          const ratesForCurrency = exchangeRates.reduce((acc, item) => {
            if (item.to_currency_code === currency_code) {
              acc++
            }

            return acc
          }, 0)

          if (ratesForCurrency === state.walletList.length - 1) {
            const amount = state.walletList.reduce((acc, item) => {
              if (item.currency_code === currency_code) {
                acc += Number(item.balance)
              } else {
                const rate = exchangeRates.find(rateItem => {
                  return rateItem.to_currency_code === currency_code && rateItem.from_currency_code === item.currency_code
                })

                acc += Number(item.balance * rate.quantity_out)
              }
              return acc
            }, 0)
            acc.push({
              id: item.id,
              amount,
              currency_code
            })
          }
          return acc
        }, [])
      } else {
        return []
      }
    }
  },
  mutations: {
    changeSearchAssets(state, value) {
      state.openSearchAssets = value
    },
    changeSelectAccount(state, value) {
      state.openSelectAccount = value
    },
    selectExchangeActiveTab(state, value) {
      state.selectExchangeActiveTab = value
    },
    changeSelectExchange(state,value) {
      state.selectExchangeOpened = value      
    },    
    logout(state) {
      state.authToken = null
      state.authInfo = null
      localStorage.removeItem('authToken')
    },      
    setAuthToken(state, authToken) {
      state.authToken = authToken
      localStorage.setItem('authToken', authToken)
    },
    ConfirmCode(state) {
      state.sendConfirmCode = !state.sendConfirmCode
    },
    changePhoneNumber (state, phone) {
      state.phone = phone
    },
    // API update
    updateWalletList(state, walletList) {
      state.walletList = walletList

      const selectedWalletId = state.currentWallet?.id || fetchWalletIdInLocalStorage.get()
      const selectedWallet = selectedWalletId
        ? walletList.find(({ id }) => id === selectedWalletId)
        : null

      state.currentWallet = selectedWallet || walletList[0] || null
      if (state.currentWallet) {
        fetchWalletIdInLocalStorage.set(state.currentWallet.id)
      }
    },
    updateAuthInfo(state, authInfo) {
      state.authInfo = authInfo
    },
    updateTextData(state, textData) {
      state.textData = textData
    },
    updateUserNotifications(state, userNotifications) {
      state.userNotifications = userNotifications
    },

    // API Update: exchange
    setAccountsIsFetched(state) {
      state.accountsLoading = false
    },
    updateExchangeFromAccount(state, exchangeFromAccount) {
      state.exchangeFromAccount = exchangeFromAccount
    },
    updateExchangeToAccount(state, exchangeToAccount) {
      state.exchangeToAccount = exchangeToAccount
    },
    updateExchangeRates(state, exchangeRates) {
      state.exchangeRates = exchangeRates
    },
    updateCurrentWallet(state, currentWallet) {
      state.currentWallet = currentWallet
      fetchWalletIdInLocalStorage.set(currentWallet.id)
    },
  },
  actions: {    
    // API requests
    async refreshWalletList(context) {
      return handleAsyncError(
        context,        
        async function() {
          return await refreshWalletList(context)
         }
      ) ()
    },
    async refreshExchangeRates(context) {
      handleAsyncError( 
        context,
        async function() {      
          context.commit("updateExchangeRates", await getExchangeRates(context.state.authToken))  
        }
      ) ()
    },    
    async refreshAuthInfo(context) {
      const authToken = context.state.authToken

      if (!authToken) {
        context.state.authInfoLoading = false
        return
      }

      context.state.authInfoLoading = true
      return handleAsyncError(
        context,
        async ()=> {
          context.commit("updateAuthInfo", await getAuthInfo(authToken).finally(() => context.state.authInfoLoading = false))
        },
        {
          shouldLogoutOnInvalidToken: () => context.state.authToken === authToken,
        }
      ) ()
    },
    async refreshTextData(context) {
      handleAsyncError( 
        context,
        async function() {      
            context.commit("updateTextData", await getTextData(context.state.authToken))  
        }
      ) ()
    },    
    async refreshUserNotifications(context) {
      handleAsyncError( 
        context,
        async function() {
          context.commit("updateUserNotifications", await getWalletUserNotifications(context.state.authToken))  
        }
      ) ()
    },
    async setDefaultExchangeAccounts(context, payload = {}) {
      handleAsyncError( 
        context,
        async function() {

          const walletList = (
            context.state.walletList === null 
            ? await refreshWalletList(context)
            : context.state.walletList
          )

          if (walletList && walletList.length > 1) {
            const firstAccount = walletList.find(({ id }) => id === payload.from_account_id) || walletList[0];
            const secondAccount = walletList.find(({ id }) => id !== firstAccount.id);

            context.commit("updateExchangeFromAccount", firstAccount);
            context.commit("updateExchangeToAccount", secondAccount);
          }

          context.commit("setAccountsIsFetched");
        }
      ) ()
    },
    async doExchange(context, payload) {
      return handleAsyncError( 
        context,
        async function() {
          return await doExchange(
            context.state.authToken, 
            payload.from_account_id,
            payload.to_account_id,
            payload.from_amount,
            payload.to_amount,
            payload.rate_id
          )
        }
      ) ()
    }
  },
  modules: {
  }
})

export default store;
